/**
 * Lightweight error monitoring wrapper around Sentry.
 *
 * Wired to fire only when VITE_SENTRY_DSN is set in the build environment.
 * In dev or any build where the DSN isn't present, this is a complete no-op
 * — no network calls, no console noise, no overhead.
 *
 * To enable in production:
 *   1. Create a Sentry project at sentry.io (free tier covers our volume).
 *   2. Copy the DSN.
 *   3. In Cloudflare Pages → sparrow-forge project → Settings → Environment
 *      variables, add `VITE_SENTRY_DSN` with the value.
 *   4. Trigger a redeploy.
 *
 * SafeBoundary calls `captureBoundaryError` whenever a render error is
 * caught, so a runtime crash on any surface posts to Sentry along with the
 * section name and component stack.
 *
 * To capture out-of-band errors (Promise rejections, network failures), call
 * `captureError(err, context)` from the relevant catch block.
 */

import * as Sentry from '@sentry/browser';

let initialized = false;

/**
 * Initialize Sentry. Idempotent — safe to call from every entry's main.tsx
 * without worrying about double-init.
 */
export function initMonitoring(section: string): void {
  if (initialized) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    /* Dev / DSN not provisioned — silently skip. */
    return;
  }
  Sentry.init({
    dsn,
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? 'sparrow-forge@dev',
    environment: (import.meta.env.MODE as string | undefined) ?? 'production',
    tracesSampleRate: 0.1,
    /* Block known noise — we don't care about extension-injected errors. */
    ignoreErrors: [
      /^ResizeObserver/,
      /^Script error/,
      /Non-Error promise rejection/,
    ],
    initialScope: {
      tags: { surface: section },
    },
  });
  initialized = true;
}

/**
 * Forward a SafeBoundary catch to Sentry. No-op if Sentry isn't initialized.
 */
export function captureBoundaryError(
  err: Error,
  info: { componentStack?: string | null; section: string }
): void {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    scope.setTag('surface', info.section);
    if (info.componentStack) {
      scope.setExtra('componentStack', info.componentStack);
    }
    Sentry.captureException(err);
  });
}

/**
 * Capture an arbitrary error with optional context.
 */
export function captureError(err: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.withScope((scope) => {
    if (context) {
      for (const [k, v] of Object.entries(context)) scope.setExtra(k, v);
    }
    Sentry.captureException(err);
  });
}

/**
 * Annotate the current user (called once per session after sign-in completes).
 */
export function setMonitoringUser(id: string, email?: string): void {
  if (!initialized) return;
  Sentry.setUser({ id, email });
}
